import React, { useCallback, useEffect, useState } from 'react';
import { admin } from '../services/api';
import { useNdf } from '../NdfContext';
import './Administration.css';

const ROLES = ['collaborateur', 'valideur', 'comptable', 'admin'];
const ONGLETS = [
  ['habilitations', '👥 Habilitations'],
  ['categories', '🏷️ Catégories'],
  ['societes', '🏢 Sociétés'],
  ['bareme', '🚗 Barème km'],
  ['journal', '📜 Journal'],
];

// Administration : qui entre, qui valide qui, les catégories avec leurs
// comptes comptables, les sociétés, le barème. Chaque changement est
// enregistré immédiatement et journalisé côté serveur.
function Administration() {
  const { recharger } = useNdf();
  const [onglet, setOnglet] = useState('habilitations');
  const [habs, setHabs] = useState([]);
  const [cats, setCats] = useState([]);
  const [socs, setSocs] = useState([]);
  const [bareme, setBareme] = useState([]);
  const [journal, setJournal] = useState([]);
  const [erreur, setErreur] = useState('');
  const [nouvelle, setNouvelle] = useState({ email: '', role: 'collaborateur', valideur_email: '', societe_id: '' });
  const [nomSociete, setNomSociete] = useState('');

  const charger = useCallback(async () => {
    try {
      const [h, c, s, b] = await Promise.all([
        admin.habilitations(), admin.categories(), admin.societes(), admin.baremes(),
      ]);
      setHabs(h.data.habilitations);
      setCats(c.data.categories);
      setSocs(s.data.societes);
      setBareme(b.data.baremes);
    } catch (err) {
      setErreur(err.response?.data?.error || 'Chargement impossible');
    }
  }, []);
  useEffect(() => { charger(); }, [charger]);
  useEffect(() => {
    if (onglet === 'journal') admin.journal().then((r) => setJournal(r.data.evenements)).catch(() => {});
  }, [onglet]);

  const agir = async (promesse) => {
    setErreur('');
    try {
      await promesse;
      await charger();
      recharger();
    } catch (err) {
      setErreur(err.response?.data?.error || 'Action impossible');
    }
  };

  const majHab = (id, champs) => agir(admin.modifierHabilitation(id, champs));
  const majCat = (id, champs) => agir(admin.modifierCategorie(id, champs));

  const ajouterHab = () => {
    if (!nouvelle.email.includes('@')) { setErreur('E-mail invalide'); return; }
    agir(admin.creerHabilitation({
      ...nouvelle,
      valideur_email: nouvelle.valideur_email || null,
      societe_id: nouvelle.societe_id ? parseInt(nouvelle.societe_id, 10) : null,
    })).then(() => setNouvelle({ email: '', role: 'collaborateur', valideur_email: '', societe_id: '' }));
  };

  return (
    <div>
      <h2 className="nf-titre-page">Administration</h2>
      {erreur && <div className="nf-erreur-bandeau">{erreur}</div>}

      <div className="ad-onglets">
        {ONGLETS.map(([code, libelle]) => (
          <button key={code} type="button"
            className={`ad-onglet ${onglet === code ? 'active' : ''}`}
            onClick={() => setOnglet(code)}>{libelle}</button>
        ))}
      </div>

      {onglet === 'habilitations' && (
        <>
          <div className="ad-ajout">
            <input type="email" placeholder="email@societe.com" value={nouvelle.email}
              onChange={(e) => setNouvelle({ ...nouvelle, email: e.target.value })} />
            <select value={nouvelle.role} onChange={(e) => setNouvelle({ ...nouvelle, role: e.target.value })}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select value={nouvelle.valideur_email} onChange={(e) => setNouvelle({ ...nouvelle, valideur_email: e.target.value })}>
              <option value="">— valideur —</option>
              {habs.filter((h) => h.actif).map((h) => <option key={h.id} value={h.email}>{h.email}</option>)}
            </select>
            <select value={nouvelle.societe_id} onChange={(e) => setNouvelle({ ...nouvelle, societe_id: e.target.value })}>
              <option value="">— société —</option>
              {socs.filter((s) => s.actif).map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
            <button type="button" className="nf-btn nf-btn--principal" onClick={ajouterHab}>Ajouter</button>
          </div>
          <div className="table-container">
            <table className="ad-table">
              <thead>
                <tr><th>E-mail</th><th>Nom</th><th>Rôle</th><th>Valideur (N+1)</th><th>Société</th><th>Actif</th></tr>
              </thead>
              <tbody>
                {habs.map((h) => (
                  <tr key={h.id} className={h.actif ? '' : 'ad-inactif'}>
                    <td>{h.email}</td>
                    <td>{[h.prenom, h.nom].filter(Boolean).join(' ') || '—'}</td>
                    <td>
                      <select value={h.role} onChange={(e) => majHab(h.id, { role: e.target.value })}>
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td>
                      <select value={h.valideur_email || ''} onChange={(e) => majHab(h.id, { valideur_email: e.target.value || null })}>
                        <option value="">—</option>
                        {habs.filter((x) => x.actif && x.email !== h.email).map((x) => (
                          <option key={x.id} value={x.email}>{x.email}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select value={h.societe_id || ''} onChange={(e) => majHab(h.id, { societe_id: e.target.value ? parseInt(e.target.value, 10) : null })}>
                        <option value="">—</option>
                        {socs.filter((s) => s.actif).map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
                      </select>
                    </td>
                    <td>
                      <input type="checkbox" checked={h.actif} onChange={(e) => majHab(h.id, { actif: e.target.checked })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {onglet === 'categories' && (
        <div className="table-container">
          <table className="ad-table">
            <thead>
              <tr><th>Catégorie</th><th>Compte comptable</th><th>TVA récupérable</th><th>Plafond TTC (€)</th><th>Active</th></tr>
            </thead>
            <tbody>
              {cats.map((c) => (
                <tr key={c.id} className={c.actif ? '' : 'ad-inactif'}>
                  <td>{c.icone} {c.libelle}{c.special === 'km' ? ' (barème)' : ''}</td>
                  <td>
                    <input type="text" defaultValue={c.compte_comptable || ''} className="ad-court"
                      onBlur={(e) => e.target.value !== (c.compte_comptable || '') && majCat(c.id, { compte_comptable: e.target.value })} />
                  </td>
                  <td>
                    <input type="checkbox" checked={c.tva_recuperable} onChange={(e) => majCat(c.id, { tva_recuperable: e.target.checked })} />
                  </td>
                  <td>
                    <input type="text" inputMode="decimal" defaultValue={c.plafond_ttc || ''} className="ad-court" placeholder="aucun"
                      onBlur={(e) => e.target.value !== String(c.plafond_ttc || '') && majCat(c.id, { plafond_ttc: e.target.value ? parseFloat(e.target.value.replace(',', '.')) : null })} />
                  </td>
                  <td>
                    <input type="checkbox" checked={c.actif} onChange={(e) => majCat(c.id, { actif: e.target.checked })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {onglet === 'societes' && (
        <>
          <div className="ad-ajout">
            <input type="text" placeholder="Nom de la société" value={nomSociete}
              onChange={(e) => setNomSociete(e.target.value)} />
            <button type="button" className="nf-btn nf-btn--principal"
              onClick={() => nomSociete.trim() && agir(admin.creerSociete({ nom: nomSociete.trim() })).then(() => setNomSociete(''))}>
              Ajouter
            </button>
          </div>
          <div className="table-container">
            <table className="ad-table">
              <thead><tr><th>Société</th><th>Code</th><th>Active</th></tr></thead>
              <tbody>
                {socs.map((s) => (
                  <tr key={s.id} className={s.actif ? '' : 'ad-inactif'}>
                    <td>{s.nom}</td>
                    <td>
                      <input type="text" defaultValue={s.code || ''} className="ad-court"
                        onBlur={(e) => e.target.value !== (s.code || '') && agir(admin.modifierSociete(s.id, { code: e.target.value }))} />
                    </td>
                    <td>
                      <input type="checkbox" checked={s.actif} onChange={(e) => agir(admin.modifierSociete(s.id, { actif: e.target.checked }))} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {onglet === 'bareme' && (
        <>
          <p className="ad-aide">Taux appliqué par kilomètre selon la puissance fiscale (tranche annuelle ≤ 5 000 km du barème fiscal, simplifiée). À faire valider par la comptabilité.</p>
          <div className="table-container">
            <table className="ad-table">
              <thead><tr><th>Puissance</th><th>Année</th><th>Taux (€/km)</th></tr></thead>
              <tbody>
                {bareme.map((b) => (
                  <tr key={b.id}>
                    <td>{b.puissance}</td>
                    <td>{b.annee}</td>
                    <td>
                      <input type="text" inputMode="decimal" defaultValue={b.taux} className="ad-court"
                        onBlur={(e) => parseFloat(e.target.value.replace(',', '.')) !== parseFloat(b.taux)
                          && agir(admin.modifierBareme(b.id, parseFloat(e.target.value.replace(',', '.'))))} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {onglet === 'journal' && (
        <div className="table-container">
          <table className="ad-table">
            <thead><tr><th>Quand</th><th>Qui</th><th>Action</th><th>Cible</th></tr></thead>
            <tbody>
              {journal.map((e) => (
                <tr key={e.id}>
                  <td className="nowrap">{new Date(e.created_at).toLocaleString('fr-FR')}</td>
                  <td>{e.acteur_email || '—'}</td>
                  <td>{e.action}</td>
                  <td>{e.cible || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Administration;
